import {OrderModel} from '@/order/models/OrderModel'
import {dayKey, relativeDayFilter, isToday, SEM_DATA_LABEL} from '@/utils/filters/DateTimeFilter'

export interface IOrderDayGroup {
    day: string
    key: string
    date?: Date
    label: string
    isToday: boolean
    isOpen: boolean
    orders: OrderModel[]
}

/**
 * Agrupa os pedidos por dia de `createdAt`, preservando a ordem devolvida pelo backend.
 *
 * O agrupamento é sequencial, e não por mapa de chaves: como a paginação é acumulativa
 * (o "Mais resultados" dá push no array), um dia partido entre duas páginas cai sozinho
 * no grupo que já estava aberto.
 *
 * `isOpen` marca o grupo cuja contagem ainda não é confiável. Só o último grupo pode
 * estar nessa situação, e apenas enquanto houver mais páginas para carregar: todos os
 * outros já tiveram o dia seguinte começando abaixo deles, o que fecha a contagem.
 */
export function groupOrdersByDay(orders:OrderModel[], hasMorePages:boolean):IOrderDayGroup[] {
    const groups:IOrderDayGroup[] = []
    let currentGroup:IOrderDayGroup|undefined

    for (const order of orders || []) {
        const createdAt = createdAtOf(order)
        const day = dayKey(createdAt)

        if (!currentGroup || currentGroup.day !== day) {
            currentGroup = {
                day,
                // O índice evita colisão de key caso o backend devolva o mesmo dia de forma não contígua.
                key: `${day}-${groups.length}`,
                date: createdAt,
                label: createdAt ? relativeDayFilter(createdAt) : SEM_DATA_LABEL,
                isToday: isToday(createdAt),
                isOpen: false,
                orders: [],
            }

            groups.push(currentGroup)
        }

        currentGroup.orders.push(order)
    }

    const lastGroup = groups[groups.length - 1]

    if (lastGroup && hasMorePages) {
        lastGroup.isOpen = true
    }

    return groups
}

// O OrderModel converte createdAt de string para Date no construtor, mas o campo
// continua tipado como Date|string|undefined.
function createdAtOf(order:OrderModel):Date|undefined {
    return order.createdAt instanceof Date ? order.createdAt : undefined
}
